import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import { PlatformOrganisationDto } from '../../api/platformApi';
import { routeBuilders } from '../../../../routes';
import { useResendOwnerInvitation } from '../../hooks/useResendOwnerInvitation';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import { ResendOwnerInvitationDialog } from './ResendOwnerInvitationDialog';

interface PlatformOrganisationListItemProps {
  organisation: PlatformOrganisationDto;
}

function getStatusChipProps(status: string) {
  switch (status) {
    case 'Active':
      return { label: 'Active', color: 'success' as const };
    case 'InvitationSent':
      return { label: 'Invited', color: 'warning' as const };
    case 'Expired':
      return { label: 'Expired', color: 'error' as const };
    case 'Inactive':
      return { label: 'Inactive', color: 'default' as const };
    default:
      return { label: status, color: 'default' as const };
  }
}

function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

export const PlatformOrganisationListItem = React.memo(
  function PlatformOrganisationListItem({
    organisation,
  }: PlatformOrganisationListItemProps) {
    const navigate = useNavigate();
    const showSnackbar = useSnackbarStore(state => state.showSnackbar);
    const chipProps = getStatusChipProps(organisation.invitationStatus);
    const [resendDialogOpen, setResendDialogOpen] = useState(false);
    const { mutate: resendInvite, isPending: isResending } =
      useResendOwnerInvitation();

    const handleResendClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setResendDialogOpen(true);
    };

    const handleResendConfirm = () => {
      resendInvite(organisation.id, {
        onSuccess: () => {
          setResendDialogOpen(false);
          showSnackbar('Invitation resent', 'success');
        },
      });
    };

    const handleResendCancel = () => {
      setResendDialogOpen(false);
    };

    return (
      <ListItemButton
        data-testid="platform-organisation-row"
        onClick={() =>
          navigate(routeBuilders.platform.organisationDetail(organisation.id))
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: { xs: 2, sm: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: stringToColor(organisation.name),
            flexShrink: 0,
          }}
        >
          {getInitials(organisation.name)}
        </Avatar>

        {/* Name — always visible */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {organisation.name}
          </Typography>
        </Box>

        {/* Owner + Created — hidden on mobile */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              width: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {organisation.ownerEmail}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ width: 100 }}
          >
            {formatDate(organisation.createdAt)}
          </Typography>
        </Box>

        {/* Status chip — always visible */}
        <Box sx={{ width: 90, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            size="small"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        {/* Resend action — reserves width so rows stay aligned */}
        <Box sx={{ width: 30, display: 'flex', justifyContent: 'flex-end' }}>
          {organisation.canResendOwnerInvite && (
            <Tooltip title="Resend invitation">
              <IconButton
                size="small"
                onClick={handleResendClick}
                data-testid="resend-owner-invitation-button"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <ResendOwnerInvitationDialog
          open={resendDialogOpen}
          email={organisation.ownerEmail}
          organisationName={organisation.name}
          isLoading={isResending}
          onConfirm={handleResendConfirm}
          onCancel={handleResendCancel}
        />
      </ListItemButton>
    );
  }
);
